import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import { accountApi } from '../../api/account'
import { RefusedError } from '../../api/client'
import { useSession } from '../../hooks/useSession'
import { useSpoke } from '../../hooks/useSpoke'
import { PasswordForm } from './PasswordForm'

export { PasswordForm } from './PasswordForm'

// /account/password. Inside the session gate and outside the feature gates: this is not a screen a
// tenant unlocks, it is how a person keeps their account. Two ways in — the link in the sidebar foot,
// and being sent here after signing in with a password an admin minted.

export function ChangePassword() {
  const spoke = useSpoke()
  const { user, refresh } = useSession()
  const navigate = useNavigate()
  const location = useLocation()
  const [refusal, setRefusal] = useState<string | null>(null)

  const forced = user?.mustChangePassword ?? false
  // Where the link was pressed from. Held here rather than in history so a refusal does not lose it.
  const back = (location.state as { from?: string } | null)?.from ?? '/'

  const change = useMutation({
    mutationFn: (to: { current: string; next: string }) => accountApi.changePassword(spoke, to.current, to.next),
    onSuccess: async () => {
      setRefusal(null)
      // Re-read the person: a successful change clears their hold, and the shell reads that flag.
      // Swallowed deliberately — the password DID change, and failing to re-read it must not be
      // reported to the person as though it had not.
      await refresh().catch(() => undefined)
      navigate(forced ? '/' : back, { replace: true })
    },
    onError: (error) => setRefusal(message(error)),
  })

  return (
    <PasswordForm
      forced={forced}
      busy={change.isPending}
      refusal={refusal}
      onSubmit={(current, next) => change.mutate({ current, next })}
      onCancel={() => navigate(back, { replace: true })}
    />
  )
}

/** The API's own words for a refusal. Never a guess at what went wrong. */
function message(error: unknown): string {
  if (error instanceof RefusedError) return error.refusal.message
  return error instanceof Error ? error.message : 'Could not reach Scout.'
}
